import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Joi from 'joi';
import { sendDistributorInquiry } from '../../../services/DistributorService';
import AvailableOn from '../../AvailableOn/AvailableOn';
import './BecomeDistributor.css';

const initialForm = {
    // Your Details
    first_name: '',
    business_name: '',
    gst_no: '',
    contact: '',
    email: '',
    address: '',
    city: '',
    pincode: '',
    state: '',
    country: '',
    // Business Details
    nature_of_business: '',
    experience: '',
    annual_revenue: '',
    ext_salesman: '',
    invest_potential: '',
    why_interested: '',
    reference_by: '',
};

const distributorSchema = Joi.object({
    first_name: Joi.string().min(2).max(50).required().messages({
        'string.empty': 'Full Name is required',
        'string.min': 'Full Name must be at least 2 characters long'
    }),
    business_name: Joi.string().min(2).max(100).required().messages({
        'string.empty': 'Business Name is required'
    }),
    gst_no: Joi.string().allow('').max(15).messages({
        'string.max': 'GST No. cannot exceed 15 characters'
    }),
    contact: Joi.string().pattern(/^[0-9]{10,15}$/).required().messages({
        'string.pattern.base': 'Contact must be a valid phone number (10-15 digits)',
        'string.empty': 'Contact number is required'
    }),
    email: Joi.string().email({ tlds: { allow: false } }).required().messages({
        'string.email': 'Please enter a valid email address',
        'string.empty': 'Email is required'
    }),
    address: Joi.string().required().messages({ 'string.empty': 'Address is required' }),
    city: Joi.string().required().messages({ 'string.empty': 'City is required' }),
    pincode: Joi.string().pattern(/^[0-9]{6}$/).required().messages({
        'string.pattern.base': 'Pincode must be 6 digits',
        'string.empty': 'Pincode is required'
    }),
    state: Joi.string().required().messages({ 'string.empty': 'State is required' }),
    country: Joi.string().required().messages({ 'string.empty': 'Country is required' }),
    nature_of_business: Joi.string().required().messages({ 'string.empty': 'Nature of Business is required' }),
    experience: Joi.string().required().messages({ 'string.empty': 'Experience is required' }),
    annual_revenue: Joi.string().required().messages({ 'string.empty': 'Annual Revenue is required' }),
    ext_salesman: Joi.number().min(0).required().messages({
        'number.base': 'Number of Salesmen must be a number',
        'string.empty': 'Existing No. of Salesmen is required'
    }),
    invest_potential: Joi.string().allow(''),
    why_interested: Joi.string().allow(''),
    reference_by: Joi.string().allow(''),
});

function BecomeDistributor() {
    const [form, setForm] = useState(initialForm);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate form
        const { error } = distributorSchema.validate(form, { abortEarly: false });
        if (error) {
            error.details.forEach((err) => {
                toast.error(err.message);
            });
            return;
        }

        setLoading(true);
        try {
            await sendDistributorInquiry(form);
            toast.success('Inquiry sent successfully! We will contact you soon.');
            setForm(initialForm);
        } catch (error) {
            console.error('Inquiry failed:', error);
            toast.error('Failed to send inquiry. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Helmet>
                <meta charSet="utf-8" />
                <title>Kuku Namkeen | Become Distributor</title>
                <link rel="canonical" href="https://kukunamkeen.com/become-distributor" />
            </Helmet>

            {/* Page Header Banner */}
            <section className="dist-header-title">
                <div className="max-w-6xl mx-auto px-4">
                    <h1>BECOME DISTRIBUTOR</h1>
                </div>
            </section>
            <img
                src="/Image/border-bottom-title.png"
                className="w-full header-title-image"
                alt=""
            />

            {/* Form Section */}
            <section className="distributor-page section-space">
                <div className="maxwidth mx-auto px-4">
                    <div className="dist-form-card">
                        <form onSubmit={handleSubmit}>

                            {/* ── Your Details ── */}
                            <h4 className="dist-section-title">Your Details</h4>
                            <div className="dist-grid dist-grid--three">

                                <div className="dist-field">
                                    <input
                                        type="text"
                                        name="first_name"
                                        id="first_name"
                                        placeholder=" "
                                        value={form.first_name}
                                        onChange={handleChange}
                                        required
                                    />
                                    <label htmlFor="first_name">Full Name <sup className="req">*</sup></label>
                                </div>

                                <div className="dist-field">
                                    <input
                                        type="text"
                                        name="business_name"
                                        id="business_name"
                                        placeholder=" "
                                        value={form.business_name}
                                        onChange={handleChange}
                                        required
                                    />
                                    <label htmlFor="business_name">Business Name <sup className="req">*</sup></label>
                                </div>

                                <div className="dist-field">
                                    <input
                                        type="text"
                                        name="gst_no"
                                        id="gst_no"
                                        placeholder=" "
                                        value={form.gst_no}
                                        onChange={handleChange}
                                    />
                                    <label htmlFor="gst_no">GST No.</label>
                                </div>

                                <div className="dist-field">
                                    <input
                                        type="tel"
                                        name="contact"
                                        id="contact"
                                        placeholder=" "
                                        value={form.contact}
                                        onChange={handleChange}
                                        required
                                    />
                                    <label htmlFor="contact">Your Mobile <sup className="req">*</sup></label>
                                </div>

                                <div className="dist-field">
                                    <input
                                        type="email"
                                        name="email"
                                        id="email"
                                        placeholder=" "
                                        value={form.email}
                                        onChange={handleChange}
                                        required
                                    />
                                    <label htmlFor="email">mail@yourmail.com <sup className="req">*</sup></label>
                                </div>

                                <div className="dist-field">
                                    <input
                                        type="text"
                                        name="address"
                                        id="address"
                                        placeholder=" "
                                        value={form.address}
                                        onChange={handleChange}
                                        required
                                    />
                                    <label htmlFor="address">Address <sup className="req">*</sup></label>
                                </div>

                            </div>

                            <div className="dist-grid dist-grid--four">
                                <div className="dist-field">
                                    <input
                                        type="text"
                                        name="city"
                                        id="city"
                                        placeholder=" "
                                        value={form.city}
                                        onChange={handleChange}
                                        required
                                    />
                                    <label htmlFor="city">City <sup className="req">*</sup></label>
                                </div>

                                <div className="dist-field">
                                    <input
                                        type="text"
                                        name="pincode"
                                        id="pincode"
                                        placeholder=" "
                                        value={form.pincode}
                                        onChange={handleChange}
                                        required
                                    />
                                    <label htmlFor="pincode">Pincode <sup className="req">*</sup></label>
                                </div>

                                <div className="dist-field">
                                    <input
                                        type="text"
                                        name="state"
                                        id="state"
                                        placeholder=" "
                                        value={form.state}
                                        onChange={handleChange}
                                        required
                                    />
                                    <label htmlFor="state">State <sup className="req">*</sup></label>
                                </div>

                                <div className="dist-field">
                                    <input
                                        type="text"
                                        name="country"
                                        id="country"
                                        placeholder=" "
                                        value={form.country}
                                        onChange={handleChange}
                                        required
                                    />
                                    <label htmlFor="country">Country <sup className="req">*</sup></label>
                                </div>

                            </div>

                            {/* ── Business Details ── */}
                            <h4 className="dist-section-title mt-8">Business Details</h4>
                            <div className="dist-grid dist-grid--four">

                                <div className="dist-field">
                                    <input
                                        type="text"
                                        name="nature_of_business"
                                        id="nature_of_business"
                                        placeholder=" "
                                        value={form.nature_of_business}
                                        onChange={handleChange}
                                        required
                                    />
                                    <label htmlFor="nature_of_business">Nature Of Business <sup className="req">*</sup></label>
                                    <span className="dist-hint">100-2000 Characters</span>
                                </div>

                                <div className="dist-field">
                                    <input
                                        type="text"
                                        name="experience"
                                        id="experience"
                                        placeholder=" "
                                        value={form.experience}
                                        onChange={handleChange}
                                        required
                                    />
                                    <label htmlFor="experience">Experience in Business <sup className="req">*</sup></label>
                                    <span className="dist-hint">100-2000 Characters</span>
                                </div>

                                <div className="dist-field">
                                    <input
                                        type="text"
                                        name="annual_revenue"
                                        id="annual_revenue"
                                        placeholder=" "
                                        value={form.annual_revenue}
                                        onChange={handleChange}
                                        required
                                    />
                                    <label htmlFor="annual_revenue">Annual Revenue <sup className="req">*</sup></label>
                                </div>

                                <div className="dist-field">
                                    <input
                                        type="number"
                                        name="ext_salesman"
                                        id="ext_salesman"
                                        placeholder=" "
                                        value={form.ext_salesman}
                                        onChange={handleChange}
                                        required
                                    />
                                    <label htmlFor="ext_salesman">Existing No. of Salesmen <sup className="req">*</sup></label>
                                </div>

                            </div>

                            <div className="dist-grid dist-grid--three">
                                <div className="dist-field">
                                    <input
                                        type="number"
                                        name="invest_potential"
                                        id="invest_potential"
                                        placeholder=" "
                                        value={form.invest_potential}
                                        onChange={handleChange}
                                    />
                                    <label htmlFor="invest_potential">Investment Potential</label>
                                </div>

                                <div className="dist-field">
                                    <input
                                        type="text"
                                        name="why_interested"
                                        id="why_interested"
                                        placeholder=" "
                                        value={form.why_interested}
                                        onChange={handleChange}
                                    />
                                    <label htmlFor="why_interested">Why you Interested?</label>
                                    <span className="dist-hint">100-2000 Characters</span>
                                </div>

                                <div className="dist-field">
                                    <input
                                        type="text"
                                        name="reference_by"
                                        id="reference_by"
                                        placeholder=" "
                                        value={form.reference_by}
                                        onChange={handleChange}
                                    />
                                    <label htmlFor="reference_by">Referenced by</label>
                                </div>

                            </div>

                            {/* Submit */}
                            <div className="mt-6">
                                <button
                                    type="submit"
                                    className="dist-submit-btn"
                                    disabled={loading}
                                >
                                    {loading ? 'Sending...' : 'Send Inquiry'}
                                </button>
                            </div>

                        </form>
                    </div>
                </div>
            </section>

            {/* Available On */}
            <AvailableOn />

            <ToastContainer position="top-right" autoClose={5000} />
        </>
    );
}

export default BecomeDistributor;
